import _ from 'lodash';

import { request } from '@/utils/infrastructure/communication';
import { buildRefreshContext, resolveAuthContextHeaders } from '@/utils/infrastructure/auth';
import { parseJson } from '../utils/infrastructure/utils';

const controller = new AbortController();
const { signal } = controller;

const twoFactorHeaders = () => {
  const headers = resolveAuthContextHeaders();
  return Object.keys(headers).length > 0 ? { headers } : {};
};

export async function authLogin({ route, headers, suppressErrorNotification, ...params }) {
  return request(route || '/auth', {
    method: 'POST',
    credentials: 'include',
    data: params,
    headers,
    signal,
    skip401Queue: true,
    ...(suppressErrorNotification ? { suppressErrorNotification: true } : {}),
  });
}

export async function authPartyLogin({ headers, isSsoFlow, ssoClientId, ...params }) {
  return request(`/auth/${params.username}`, {
    method: 'POST',
    credentials: 'include',
    data: {
      ...params,
      ...(isSsoFlow && { isSsoFlow: true }),
      ...(isSsoFlow && ssoClientId && { ssoClientId }),
    },
    ...(headers && { headers }),
    signal,
    skip401Queue: true,
  });
}

export async function sendTwoFactorAuthCode(data) {
  return request('/auth/sendTwoFactorAuthCode', {
    method: 'POST',
    data,
    ...twoFactorHeaders(),
    signal,
    skip401Queue: true,
  });
}

export async function validateSecurityCode(data) {
  return request('/auth/validateCode', {
    method: 'POST',
    data,
    ...twoFactorHeaders(),
    signal,
    skip401Queue: true,
  });
}

/**
 * Refreshes an access token.
 *
 * @param {string|Object} token - Current token (string or parsed object)
 * @param {{ appId?: string|null, portal?: Object|null }|null} contextOverride -
 *
 *   When provided, uses these values instead of reading from the current active session.
 *   Required when the session being refreshed differs from the tab's active session
 *   (e.g. an APP token refresh triggered while AUTOMY is the active session).
 */
export async function authRefreshToken(token, contextOverride = null) {
  const { accessToken, refreshToken } = parseJson(token);

  const context = contextOverride || buildRefreshContext();
  const appId = context.appId ?? null;
  const portal = context.portal ?? null;

  const headers = {};

  if (appId) headers.appId = appId;
  else if (portal) headers.portalId = portal.id;

  return request('/auth/refreshToken', {
    method: 'POST',
    data: { accessToken, refreshToken },
    ...(Object.keys(headers).length > 0 && { headers }),
    signal,
    skip401Queue: true,
  });
}

export async function accountConfirm({ token }) {
  return request('/auth/confirmAccount', {
    method: 'POST',
    data: { token },
    signal,
    skip401Queue: true,
  });
}

export async function authLogout() {
  return request('/auth/logout', {
    method: 'POST',
    signal,
    skip401Queue: true,
  });
}

export async function validateSsoClient({ clientId, redirectUrl }) {
  return request('/auth/sso/validateClient', {
    method: 'POST',
    data: { clientId, redirectUrl },
    signal,
    skip401Queue: true,
  });
}
